import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { Box } from '@mui/material'
import { useIntl } from 'react-intl'

import { tokens, shortScreen } from '@configuration/theme'
import Button from '@base/Button'
import InlineMessage from '@base/InlineMessage'
import BackButton from '@components/BackButton'
import SplitAuthLayout from '@components/SplitAuthLayout'
import AuthError from '@components/AuthError'
import AuthLoading from '@components/AuthLoading'
import {
  clearRecoveryJustCompleted,
  clearRecoveryReturnTo,
  peekRecoveryReturnTo,
} from '@utils/recovery-handoff.utils'
import { useSignOut } from '@hooks/useSignOut'
import { KRATOS_MSG_SETTINGS_UPDATED, translateKratosText } from '@i18n/kratos/kratos-i18n'
import { getFlowMessages } from '@utils/kratos-nodes.utils'
import { isNonScriptUrl } from '@utils/url.utils'

import ProfilePanel from './components/ProfilePanel'
import PasswordPanel from './components/PasswordPanel'
import SettingsTabs, {
  settingsTabId,
  settingsTabPanelId,
} from './components/SettingsTabs/SettingsTabs.component'
import TwoFactorPanel from './components/TwoFactorPanel'

import {
  clearSubmittedContext,
  readActiveTab,
  readSubmittedContext,
  type SettingsTab,
  type SubmittedContext,
  writeActiveTab,
} from './SettingsPage.utils'

import { useSettingsFlow } from './hooks/useSettingsFlow'
import { useLocaleReconcile } from './hooks/useLocaleReconcile'

// Long enough to register the success banner, short enough to not feel stuck.
const RETURN_REDIRECT_DELAY_MS = 800

const SettingsPage = () => {
  const intl = useIntl()
  const location = useLocation()
  const params = useMemo(() => new URLSearchParams(location.search), [location.search])
  const flowId = params.get('flow')
  const initialReturnTo = params.get('return_to')

  // Peek once at mount so wrong-password retries don't redrive the read.
  // See recovery-handoff.ts for why the stash exists.
  const recoveryStashedReturnTo = useMemo(() => peekRecoveryReturnTo(), [])
  const effectiveInitialReturnTo = initialReturnTo ?? recoveryStashedReturnTo

  const { flow, csrfValue, error, loading } = useSettingsFlow(flowId, effectiveInitialReturnTo)

  // flow.return_to is allowlist-checked by Kratos. The stash bypasses that
  // server-side allowlist, so trust it only because customer-api built the
  // URL from PRODUCT_URL (admin-controlled). isNonScriptUrl is the scheme
  // scrub on both paths.
  const flowReturnTo =
    typeof flow?.return_to === 'string' && isNonScriptUrl(flow.return_to) ? flow.return_to : null
  const stashReturnTo =
    recoveryStashedReturnTo && isNonScriptUrl(recoveryStashedReturnTo)
      ? recoveryStashedReturnTo
      : null
  const safeReturnTo = flowReturnTo ?? stashReturnTo

  const [activeTab, setActiveTab] = useState<SettingsTab>(readActiveTab)
  const [submittedContext, setSubmittedContext] = useState<SubmittedContext | null>(
    readSubmittedContext,
  )
  const { signOut, error: signOutError, pending: signOutPending } = useSignOut()

  // Persist tab changes through a single effect so future call sites (e.g. a
  // URL-based handler) don't have to remember to mirror writeActiveTab.
  useEffect(() => {
    writeActiveTab(activeTab)
  }, [activeTab])

  // Consume the recovery handoff after the first commit. The lazy useState
  // initializer above only peeks (see recovery-handoff.ts) so React 18's
  // StrictMode dev-mode double-invocation of that initializer doesn't drop
  // the signal on the second pass. Clearing here, post-commit, runs once in
  // prod and is idempotent under StrictMode's effect cleanup-and-rerun.
  useEffect(() => {
    clearRecoveryJustCompleted()
  }, [])

  const handleTabChange = (next: SettingsTab): void => {
    setActiveTab(next)
    // Moving away from the tab that just submitted dismisses the success UI
    // — otherwise the banner + sign-out CTA leak across tabs (flow.ui.messages
    // is flow-level, doesn't know which method produced 1050001).
    if (submittedContext && submittedContext.tab !== next) {
      clearSubmittedContext()
      setSubmittedContext(null)
    }
  }

  // `flow` is already CSRF/origin-validated by useSettingsFlow when non-null.
  const flowSucceeded =
    flow !== null && (flow.ui.messages || []).some((m) => m.id === KRATOS_MSG_SETTINGS_UPDATED)
  const justUpdated =
    flowSucceeded &&
    flow !== null &&
    submittedContext !== null &&
    submittedContext.flowId === flow.id &&
    submittedContext.tab === activeTab

  useLocaleReconcile(flow, justUpdated, submittedContext)

  // Drops the persisted submit context so an F5 / fresh tab / Back-after-
  // redirect doesn't replay yesterday's "Saved." or re-trigger the auto-
  // redirect (flow.return_to + 1050001 stay on the Kratos flow forever).
  // Local React state keeps the banner visible for the current mount.
  useEffect(() => {
    if (justUpdated) {
      clearSubmittedContext()
    }
  }, [justUpdated])

  // Auto-redirect applies to both tabs — if the user arrived with return_to,
  // they expect to be returned after ANY save.
  useEffect(() => {
    if (!justUpdated || !safeReturnTo) {
      return undefined
    }
    const timer = window.setTimeout(() => {
      clearRecoveryReturnTo()
      window.location.replace(safeReturnTo)
    }, RETURN_REDIRECT_DELAY_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [justUpdated, safeReturnTo])

  if (loading) {
    return <AuthLoading showLanguageSwitcher={false} />
  }

  if (error || !flow) {
    return (
      <AuthError
        welcome={intl.formatMessage({ id: 'settings.error.welcome' })}
        message={error || intl.formatMessage({ id: 'settings.expired' })}
        showLanguageSwitcher={false}
      />
    )
  }

  const flowMessages = getFlowMessages(flow)

  return (
    <SplitAuthLayout
      dataTestId='settings-page'
      showLanguageSwitcher={false}
      back={
        safeReturnTo ? (
          <BackButton
            href={safeReturnTo}
            aria-label={intl.formatMessage({ id: 'settings.return.backAria' })}
          >
            {intl.formatMessage({ id: 'settings.return.back' })}
          </BackButton>
        ) : undefined
      }
    >
      <Box>
        <Box
          component='h1'
          sx={{
            fontWeight: 500,
            fontSize: 32,
            letterSpacing: '-0.64px',
            m: 0,
            [shortScreen.compact]: { fontSize: 28 },
            [shortScreen.tight]: { fontSize: 24 },
          }}
        >
          {intl.formatMessage({ id: 'settings.pagebar.title' })}
        </Box>
        <Box sx={{ color: tokens.businessBlack60, fontSize: 14, mt: 1 }}>
          {intl.formatMessage({ id: 'settings.pagebar.lede' })}
        </Box>
      </Box>

      <SettingsTabs value={activeTab} onChange={handleTabChange} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {justUpdated && (
          <InlineMessage variant='success'>
            {intl.formatMessage({ id: 'settings.successAlert' })}
          </InlineMessage>
        )}
        {flowMessages.map((m) => (
          <InlineMessage key={m.id} variant={m.type === 'error' ? 'error' : 'info'}>
            {translateKratosText(m, intl)}
          </InlineMessage>
        ))}

        <Box
          role='tabpanel'
          id={settingsTabPanelId(activeTab)}
          aria-labelledby={settingsTabId(activeTab)}
          tabIndex={0}
        >
          {activeTab === 'profile' && <ProfilePanel flow={flow} csrfValue={csrfValue} />}
          {activeTab === 'password' && <PasswordPanel flow={flow} csrfValue={csrfValue} />}
          {activeTab === 'twoFactor' && <TwoFactorPanel />}
        </Box>

        {/* Nudge to re-authenticate with the new password (Kratos keeps the
         * session alive). Suppressed when a return-redirect is queued — the
         * CTA would compete with the timer. */}
        {justUpdated && activeTab === 'password' && !safeReturnTo && (
          <>
            {signOutError && <InlineMessage variant='error'>{signOutError}</InlineMessage>}
            <Button
              variant='bordered'
              size='lg'
              block={true}
              type='button'
              onClick={signOut}
              disabled={signOutPending}
            >
              {intl.formatMessage({
                id: signOutPending ? 'common.signOut.pending' : 'common.signOut.label',
              })}
            </Button>
          </>
        )}
      </Box>
    </SplitAuthLayout>
  )
}

export default SettingsPage
